import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Download,
  Trash2,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  User,
  Calendar,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Info,
  Link,
  Check,
  RotateCw
} from 'lucide-react';
import type { WorkspaceFile } from '@sahaj/types';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { fileResourceService } from '../../lib/FileResourceService';
import { getPresignedDownloadUrl } from '../attachments/attachmentCache';

interface FilePreviewModalProps {
  file: WorkspaceFile | null;
  filesList?: WorkspaceFile[];
  isOpen: boolean;
  onClose: () => void;
  onSelectFile?: (file: WorkspaceFile) => void;
  onDeleteSuccess?: (fileId: string) => void;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  file,
  filesList = [],
  isOpen,
  onClose,
  onSelectFile,
  onDeleteSuccess
}) => {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [zoomScale, setZoomScale] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);

  const currentIndex = file && filesList.length > 0 ? filesList.findIndex((f) => f.id === file.id) : -1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < filesList.length - 1;

  const handlePrevious = useCallback(() => {
    if (hasPrevious && onSelectFile) {
      const prevFile = filesList[currentIndex - 1];
      if (prevFile) onSelectFile(prevFile);
    }
  }, [hasPrevious, currentIndex, filesList, onSelectFile]);

  const handleNext = useCallback(() => {
    if (hasNext && onSelectFile) {
      const nextFile = filesList[currentIndex + 1];
      if (nextFile) onSelectFile(nextFile);
    }
  }, [hasNext, currentIndex, filesList, onSelectFile]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, handlePrevious, handleNext]);

  const isImage = file?.fileType?.startsWith('image/') || file?.fileCategory === 'IMAGE';
  const isPdf = file?.fileType === 'application/pdf' || file?.fileName.toLowerCase().endsWith('.pdf');
  const isVideo = file?.fileType?.startsWith('video/') || file?.fileCategory === 'VIDEO';
  const isAudio = file?.fileType?.startsWith('audio/') || file?.fileCategory === 'AUDIO';

  useEffect(() => {
    let active = true;

    if (!isOpen || !file || !currentWorkspace) {
      setBlobUrl(null);
      setIsLoading(false);
      setHasError(false);
      setZoomScale(1);
      setRotation(0);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    getPresignedDownloadUrl(currentWorkspace.id, file.id)
      .then((data) => {
        if (!active) return;
        setBlobUrl(data.url);
        setIsLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.warn('[FilePreviewModal] presigned URL resolve error:', err);
        const fallbackUrl = fileResourceService.getResourceUrl(currentWorkspace.id, file.id, 'stream');
        setBlobUrl(fallbackUrl);
        setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isOpen, file?.id, currentWorkspace?.id]);

  if (!isOpen || !file || !currentWorkspace) return null;

  const downloadUrl = blobUrl || fileResourceService.getResourceUrl(currentWorkspace.id, file.id, 'download');
  const isUploader = file.uploader?.id === user?.id;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${file.fileName}"?`)) return;
    try {
      await api.files.delete(currentWorkspace.id, file.id);
      if (onDeleteSuccess) {
        onDeleteSuccess(file.id);
      }
      onClose();
    } catch (err) {
      console.error('Failed to delete file', err);
      alert('Failed to delete file');
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/app/files?fileId=${file.id}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md p-3 sm:p-5 animate-in fade-in select-none">
      <div className="w-full max-w-6xl max-h-[92vh] rounded-2xl bg-card border border-border shadow-2xl overflow-hidden flex flex-col relative">
        {/* Previous / Next Overlays */}
        {hasPrevious && (
          <button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-card/80 hover:bg-card text-foreground border border-border flex items-center justify-center shadow-lg transition backdrop-blur"
            title="Previous File (Left Arrow)"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}

        {hasNext && (
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-card/80 hover:bg-card text-foreground border border-border flex items-center justify-center shadow-lg transition backdrop-blur"
            title="Next File (Right Arrow)"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        {/* Header Bar */}
        <div className="px-6 py-3.5 border-b border-border bg-card flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 shadow-sm">
              {isImage ? (
                <ImageIcon className="h-5 w-5" />
              ) : isVideo ? (
                <Film className="h-5 w-5" />
              ) : isAudio ? (
                <Music className="h-5 w-5" />
              ) : (
                <FileText className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-foreground truncate max-w-md">{file.fileName}</h3>
              <p className="text-xs text-muted-foreground">{formatFileSize(file.fileSize)} • {file.fileType}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isImage && (
              <div className="flex items-center gap-1 bg-muted px-2 py-1 rounded-xl border border-border mr-1">
                <button
                  onClick={() => setZoomScale((z) => Math.max(0.5, z - 0.25))}
                  className="p-1 text-muted-foreground hover:text-foreground transition"
                  title="Zoom Out"
                >
                  <ZoomOut className="h-3.5 w-3.5" />
                </button>
                <span className="text-[11px] font-mono w-10 text-center text-foreground">{Math.round(zoomScale * 100)}%</span>
                <button
                  onClick={() => setZoomScale((z) => Math.min(3, z + 0.25))}
                  className="p-1 text-muted-foreground hover:text-foreground transition"
                  title="Zoom In"
                >
                  <ZoomIn className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  className="p-1 text-muted-foreground hover:text-foreground transition ml-1"
                  title="Rotate Right"
                >
                  <RotateCw className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => { setZoomScale(1); setRotation(0); }}
                  className="p-1 text-muted-foreground hover:text-foreground transition ml-1"
                  title="Reset Zoom & Rotation"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            <button
              onClick={handleCopyLink}
              className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition"
              title="Copy Link"
            >
              {copiedLink ? <Check className="h-4 w-4 text-emerald-500" /> : <Link className="h-4 w-4" />}
            </button>

            <button
              onClick={() => setShowDetails((prev) => !prev)}
              className={`p-2 rounded-xl transition ${
                showDetails ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              title="Toggle File Details"
            >
              <Info className="h-4 w-4" />
            </button>

            <a
              href={downloadUrl}
              download={file.fileName}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition shadow-sm"
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </a>

            {isUploader && (
              <button
                onClick={handleDelete}
                className="p-2 rounded-xl text-destructive hover:bg-destructive/10 transition"
                title="Delete file"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}

            <button
              onClick={onClose}
              className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition ml-1"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content Viewer + Optional Side Details Panel */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* Main Viewer */}
          <div className="flex-1 overflow-auto bg-muted/30 p-6 flex items-center justify-center min-h-[420px]">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-xs font-medium">Loading secure resource stream...</p>
              </div>
            ) : isImage ? (
              <div className="max-h-[68vh] flex items-center justify-center overflow-auto p-4">
                {hasError ? (
                  <div className="p-8 text-center max-w-sm">
                    <div className="w-16 h-16 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center mx-auto mb-4 text-destructive shadow-sm">
                      <ImageIcon className="h-8 w-8" />
                    </div>
                    <h4 className="text-base font-bold text-foreground mb-1 truncate">{file.fileName}</h4>
                    <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
                      Unable to load image preview.
                    </p>
                    <a
                      href={downloadUrl}
                      download={file.fileName}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition shadow-sm"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download Image</span>
                    </a>
                  </div>
                ) : (
                  <img
                    src={blobUrl || downloadUrl}
                    alt={file.fileName}
                    style={{
                      transform: `scale(${zoomScale}) rotate(${rotation}deg)`,
                      transition: 'transform 0.15s ease-out'
                    }}
                    onError={() => setHasError(true)}
                    className="max-h-[62vh] max-w-full rounded-xl object-contain shadow-lg border border-border bg-card"
                  />
                )}
              </div>
            ) : isPdf ? (
              <div className="w-full h-[68vh] rounded-xl overflow-hidden border border-border shadow-sm bg-white">
                {blobUrl ? (
                  <iframe
                    src={blobUrl}
                    title={file.fileName}
                    className="w-full h-full border-none rounded-xl"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                    <AlertCircle className="h-10 w-10 text-amber-500 mb-2" />
                    <p className="text-sm font-semibold">Unable to stream PDF preview natively.</p>
                    <a
                      href={downloadUrl}
                      download={file.fileName}
                      className="mt-4 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold"
                    >
                      Download PDF
                    </a>
                  </div>
                )}
              </div>
            ) : isVideo ? (
              <video
                controls
                autoPlay={false}
                className="max-h-[62vh] max-w-full rounded-xl border border-border shadow-lg"
              >
                <source src={downloadUrl} type={file.fileType} />
                Your browser does not support HTML5 video element.
              </video>
            ) : isAudio ? (
              <div className="w-full max-w-md p-8 rounded-2xl bg-card border border-border text-center shadow-lg">
                <Music className="h-16 w-16 text-primary mx-auto mb-4 animate-pulse" />
                <p className="text-sm font-bold text-foreground mb-1 truncate">{file.fileName}</p>
                <p className="text-xs text-muted-foreground mb-6">{formatFileSize(file.fileSize)}</p>
                <audio controls className="w-full">
                  <source src={downloadUrl} type={file.fileType} />
                  Your browser does not support audio element.
                </audio>
              </div>
            ) : (
              <div className="p-8 text-center max-w-sm">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4 text-primary shadow-sm">
                  <FileText className="h-8 w-8" />
                </div>
                <h4 className="text-base font-bold text-foreground mb-1 truncate">{file.fileName}</h4>
                <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
                  Preview is not available directly in browser for this file format.
                </p>
                <a
                  href={downloadUrl}
                  download={file.fileName}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition shadow-sm"
                >
                  <Download className="h-4 w-4" />
                  <span>Download to View</span>
                </a>
              </div>
            )}
          </div>

          {/* Toggleable Details Side Panel */}
          {showDetails && (
            <div className="w-72 border-l border-border bg-card p-5 overflow-y-auto flex flex-col gap-4 text-xs animate-in slide-in-from-right duration-200">
              <h4 className="font-bold text-foreground text-sm border-b border-border pb-2">File Details</h4>

              <div>
                <span className="text-muted-foreground block mb-0.5">Filename</span>
                <span className="font-semibold text-foreground break-all">{file.fileName}</span>
              </div>

              <div>
                <span className="text-muted-foreground block mb-0.5">Category & Type</span>
                <span className="font-mono bg-muted px-2 py-0.5 rounded text-[11px] font-semibold text-foreground uppercase">
                  {file.fileCategory || 'OTHER'}
                </span>
                <span className="text-muted-foreground block mt-1">{file.fileType}</span>
              </div>

              <div>
                <span className="text-muted-foreground block mb-0.5">File Size</span>
                <span className="font-semibold text-foreground font-mono">{formatFileSize(file.fileSize)}</span>
              </div>

              <div>
                <span className="text-muted-foreground block mb-0.5">Uploaded By</span>
                <span className="font-semibold text-foreground flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-primary" />
                  {file.uploader?.displayName || 'User'}
                </span>
              </div>

              <div>
                <span className="text-muted-foreground block mb-0.5">Created Date</span>
                <span className="font-medium text-foreground flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {formatDate(file.createdAt)}
                </span>
              </div>

              <div>
                <span className="text-muted-foreground block mb-0.5">Shared Location</span>
                <span className="font-medium text-foreground">
                  {file.channelName ? `#${file.channelName}` : file.conversationId ? 'Direct Message' : 'Workspace File'}
                </span>
              </div>

              <div>
                <span className="text-muted-foreground block mb-0.5">Status & Visibility</span>
                <span className="inline-block bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold px-2 py-0.5 rounded text-[10px] uppercase">
                  {file.status || 'READY'} • {file.visibility || 'WORKSPACE'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Metadata Footer */}
        <div className="px-6 py-3 border-t border-border bg-card text-xs text-muted-foreground flex items-center justify-between flex-wrap gap-4 flex-shrink-0">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
              Uploaded by <strong className="text-foreground">{file.uploader?.displayName || 'User'}</strong>
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              {formatDate(file.createdAt)}
            </span>
          </div>

          {file.channelName && (
            <span className="bg-muted px-2.5 py-1 rounded-lg text-foreground font-mono text-[11px] border border-border/60">
              #{file.channelName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
